import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Autocomplete,
  Paper,
  Stack,
  CircularProgress
} from "@mui/material";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";

const ProductManager = ({ onProductDataChange }) => {
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const [selectedSection, setSelectedSection] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");

  const [sectionSeq, setSectionSeq] = useState("");
  const [categorySeq, setCategorySeq] = useState("");
  const [brandSeq, setBrandSeq] = useState("");

  const [productCodeMode, setProductCodeMode] = useState("manual");
  const [autoGenerateCode, setAutoGenerateCode] = useState(false);

  const [loadingSections, setLoadingSections] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  async function loadInitialData() {
    await Promise.all([loadCodeMode(), loadSections(), loadBrands()]);
  }

async function loadCodeMode() {
  const response = await fetch("/productos/modo-codigo/");
  const data = await response.json();

  const auto = data?.auto_generate_code === true;

  setProductCodeMode(auto ? "automatico" : "manual");
  setAutoGenerateCode(auto);
}

  async function loadSections() {
    try {
      setLoadingSections(true);
      const response = await fetch("/categorias/secciones/");
      const data = await response.json();
      setSections(Array.isArray(data) ? data : []);
    } finally {
      setLoadingSections(false);
    }
  }

  async function loadBrands() {
    try {
      setLoadingBrands(true);
      const response = await fetch("/categorias/marcas/");
      const data = await response.json();
      setBrands(Array.isArray(data) ? data : []);
    } finally {
      setLoadingBrands(false);
    }
  }

  async function loadCategories(sectionId) {
    try {
      setLoadingCategories(true);
      const response = await fetch(`/categorias/seccion/${sectionId}/`);
      const data = await response.json();
      setCategories(Array.isArray(data) ? data : []);
      setSelectedCategory("");
      setSelectedBrand("");
      setCategorySeq("");
      setBrandSeq("");
    } finally {
      setLoadingCategories(false);
    }
  }

  useEffect(() => {
    if (selectedSection) {
      const sec = sections.find((s) => s.id === selectedSection);
      setSectionSeq(sec ? sec.sequential_code : "");
      loadCategories(selectedSection);
    } else {
      setCategories([]);
      setSelectedCategory("");
      setSelectedBrand("");
      setSectionSeq("");
      setCategorySeq("");
      setBrandSeq("");
    }
  }, [selectedSection, sections]);

  useEffect(() => {
    if (selectedCategory) {
      const cat = categories.find((c) => c.id === selectedCategory);
      setCategorySeq(cat ? cat.sequential_code : "");
    } else {
      setCategorySeq("");
      setSelectedBrand("");
    }
  }, [selectedCategory, categories]);

  useEffect(() => {
    if (selectedBrand) {
      const br = brands.find((b) => b.id === selectedBrand);
      setBrandSeq(br ? br.sequential_code : "");
    } else {
      setBrandSeq("");
    }
  }, [selectedBrand, brands]);

  useEffect(() => {
    onProductDataChange({
      section: selectedSection,
      category: selectedCategory,
      brand: selectedBrand,
      section_seq: sectionSeq,
      category_seq: categorySeq,
      brand_seq: brandSeq,
      product_code_mode: productCodeMode,
      auto_generate_code: autoGenerateCode
    });
  }, [
    selectedSection,
    selectedCategory,
    selectedBrand,
    sectionSeq,
    categorySeq,
    brandSeq,
    productCodeMode,
    autoGenerateCode,
    onProductDataChange
  ]);

  const selectedSectionObj = useMemo(
    () => sections.find((s) => s.id === selectedSection) || null,
    [sections, selectedSection]
  );

  const selectedCategoryObj = useMemo(
    () => categories.find((c) => c.id === selectedCategory) || null,
    [categories, selectedCategory]
  );

  const selectedBrandObj = useMemo(
    () => brands.find((b) => b.id === selectedBrand) || null,
    [brands, selectedBrand]
  );

  const sortedSections = useMemo(
    () => [...sections].sort((a, b) => a.name.localeCompare(b.name)),
    [sections]
  );

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.name.localeCompare(b.name)),
    [categories]
  );

  const sortedBrands = useMemo(
    () => [...brands].sort((a, b) => a.name.localeCompare(b.name)),
    [brands]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)"
      }}
    >
      <Stack spacing={2.5}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: "#1565C0" }}>
            Gestión de Productos
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={sortedSections}
              getOptionLabel={(option) => option.name || ""}
              value={selectedSectionObj}
              onChange={(e, value) => setSelectedSection(value ? value.id : "")}
              loading={loadingSections}
              noOptionsText="No hay secciones"
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Seleccionar Sección"
                  required
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingSections ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              options={sortedCategories}
              getOptionLabel={(option) => option.name || ""}
              value={selectedCategoryObj}
              onChange={(e, value) => setSelectedCategory(value ? value.id : "")}
              loading={loadingCategories}
              noOptionsText={selectedSection ? "No hay categorías" : "Selecciona una sección primero"}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={!selectedSection}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Seleccionar Categoría"
                  required
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCategories ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              options={sortedBrands}
              getOptionLabel={(option) => option.name || ""}
              value={selectedBrandObj}
              onChange={(e, value) => setSelectedBrand(value ? value.id : "")}
              loading={loadingBrands}
              noOptionsText={selectedCategory ? "No hay marcas" : "Selecciona una categoría primero"}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={!selectedCategory}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Seleccionar Marca"
                  required
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingBrands ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </Stack>
    </Paper>
  );
};

export default ProductManager;